import { get } from 'lodash';
import { Record, List, Map } from 'immutable';

import QuestionRecord from './Question';
import RequestRecord from './Request';
import ResponseRecord, { ResponseProps } from './Response';
import {
  TestimonialRespondent,
  TestimonialRespondentStatus,
  TestimonialQuestion,
  TestimonialRequest,
  TestimonialAttachment,
} from '~/types/models/testimonial';
import { createRecordIfNotNull } from '~/App/Records/util';

export const RespondentRecord = Record(
  {
    uuid: undefined,
    name: undefined,
    email: undefined,
    status: TestimonialRespondentStatus.InProgress,
    viewed: false,
    user: undefined,
    request_position_title: false,
    position: undefined,
    questions: List<QuestionRecord>(),
    requests: [],
    updated_at: undefined,
    submitted_at: undefined,
  },
  'RespondentRecord'
);

function createQuestionsRecordList(questions?: TestimonialQuestion[]) {
  const questionsRecordList = questions?.map((question, index) => {
    return new QuestionRecord({
      ...question,
      index,
    });
  });

  return List(questionsRecordList ?? []);
}

export function createRequestsRecordList(requests?: {
  links: TestimonialRequest[];
  files: TestimonialRequest[];
}) {
  const fileRequests = get(requests, 'files');
  const linkRequests = get(requests, 'links');

  const fileRequestsRecordList =
    fileRequests?.map((request, index) => {
      return new RequestRecord({ ...request, index });
    }) ?? [];
  const linkRequestsRecordList =
    linkRequests?.map((request, index) => {
      return new RequestRecord({ ...request, index });
    }) ?? [];

  return Map({
    files: List(fileRequestsRecordList),
    links: List(linkRequestsRecordList),
  });
}

interface RespondentProps extends Partial<TestimonialRespondent> { }

export default class Respondent extends RespondentRecord {
  constructor(args: RespondentProps) {
    super({
      ...args,
      uuid: get(args, 'uuid'),
      name: get(args, 'name'),
      email: get(args, 'email'),
      status: get(args, 'status'),
      viewed: get(args, 'viewed'),
      user: get(args, 'user'),
      postion: get(args, 'position'),
      request_position_title: get(args, 'request_position_title'),
      questions: createQuestionsRecordList(get(args, 'questions')),
      requests: createRequestsRecordList(get(args, 'requests')),
      updated_at: get(args, 'updated_at'),
      submitted_at: get(args, 'submitted_at'),
    });
  }

  isSubmitted(): boolean {
    return !!this.get('submitted_at');
  }

  getQuestions(): List<QuestionRecord> {
    return this.get('questions');
  }

  getQuestion(index: number): QuestionRecord {
    return this.getQuestions().get(index);
  }

  getQuestionByUuid(id: string): QuestionRecord {
    return this.getQuestions().find((value?: QuestionRecord) => {
      return value?.get('uuid') === id;
    });
  }

  getRequestFileByUuid(uuid: string): RequestRecord {
    return this.getRequests()
      .get('files')
      .find((value) => value.get('uuid') === uuid);
  }

  getHasPositionTitle(): boolean {
    return this.get('request_position_title');
  }

  getRequests(): Map<string, List<RequestRecord>> {
    return this.get('requests', Map());
  }

  setResponse(questionId: string, response?: ResponseProps) {
    const question = this.getQuestionByUuid(questionId);
    const newResponse = response
      ? createRecordIfNotNull(response, ResponseRecord)
      : undefined;

    return this.setIn(
      ['questions', question.get('index'), 'response'],
      newResponse
    ) as Respondent;
  }

  setRequestResponse(
    requestUuid: string,
    requestResponse?: TestimonialAttachment
  ) {
    const request = this.getRequestFileByUuid(requestUuid);
    const newRequest = request.setIn(['response'], requestResponse);

    return this.setIn(
      ['requests', 'files', request.get('index')],
      newRequest
    ) as Respondent;
  }
}
